import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Form } from "react-bootstrap";
import {
  fetchSubCategories,
  deleteSubCategory,
  updateSubCategoryStatus,
  clearError,
  clearSuccess,
} from "../../redux/slices/subCategorySlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import SubCategoryModal from "./SubCategoryModal";
import DynamicBadge from "../Common/DynamicBadge";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import { FaPencilAlt, FaRegTrashAlt, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const SubCategory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    subCategories = [],
    isLoading,
    error,
    success,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.subCategories);

  const { categories = [] } = useSelector((state) => state.categories);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [subCategoryToDelete, setSubCategoryToDelete] = useState(null);

  const filteredSubCategories = categoryFilter
    ? subCategories.filter((sub) => sub.category_id === categoryFilter)
    : subCategories;

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSubCategories());
    // Clear any existing messages when component mounts
    dispatch(clearError());
    dispatch(clearSuccess());
    // Reset refs on mount to allow new toasts
    lastErrorRef.current = null;
    lastSuccessRef.current = null;
  }, [dispatch]);

  // Clear messages and show toast notifications
  useEffect(() => {
    if (error) {
      // Handle both string and object error messages
      const errorMessage =
        typeof error === "string"
          ? error
          : error.message || "An error occurred";
      toast.error(errorMessage);
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
    };
  }, [dispatch]);

  const handleCreateSubCategory = () => {
    setEditingSubCategory(null);
    setShowModal(true);
  };

  const handleEditSubCategory = (subCategoryId) => {
    setEditingSubCategory(subCategoryId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSubCategory(null);
    // Delay refetch to prevent flickering - Redux state is already updated
    // Only refetch to ensure data consistency
    setTimeout(() => {
      dispatch(fetchSubCategories());
    }, 300);
  };

  const handleDeleteSubCategory = (subCategoryId, subCategoryName) => {
    setSubCategoryToDelete({ id: subCategoryId, name: subCategoryName });
    setShowConfirmModal(true);
  };

  const confirmDeleteSubCategory = async () => {
    if (subCategoryToDelete) {
      try {
        await dispatch(deleteSubCategory(subCategoryToDelete.id)).unwrap();
        toast.success(
          `Subcategory "${subCategoryToDelete.name}" deleted successfully`
        );
        // Refresh subcategories list after successful deletion
        dispatch(fetchSubCategories());
        setShowConfirmModal(false);
        setSubCategoryToDelete(null);
      } catch (error) {
        console.error("Failed to delete subcategory:", error);
        toast.error(
          error?.message ||
          `Failed to delete subcategory "${subCategoryToDelete.name}"`
        );
      }
    }
  };

  const cancelDeleteSubCategory = () => {
    setShowConfirmModal(false);
    setSubCategoryToDelete(null);
  };

  const handleViewSubCategory = (subCategoryId) => {
    navigate(`/sub-category/details/${subCategoryId}`);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  // Table headers - simplified like categories page
  const headers = [
    {
      prop: "name",
      title: "Subcategory Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/sub-category/details/${row.id}`);
          }}
        >
          {row.name}
        </span>
      ),
    },
    {
      prop: "image_url",
      title: "Subcategory Image",
      cell: (row) => (
        <img
          src={row.image_url || process.env.PUBLIC_URL + "/not-img-found.png"} // ✅ fallback if image_url missing
          alt={row.name}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "4px",
            marginRight: "12px",
          }}
          onError={(e) => {
            e.target.onerror = null; // prevent infinite loop
            e.target.src = process.env.PUBLIC_URL + "/not-img-found.png"; // ✅ fallback image if loading fails
          }}
        />
      ),
    },
    {
      prop: "category_id",
      title: "Category",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={getCategoryName(row.category_id)} />
      ),
    },
    {
      prop: "description",
      title: "Description",
      cell: (row) => (
        <div
          style={{
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.description}
        >
          {row.description || "No description"}
        </div>
      ),
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={row.status === "Active" ? "success" : "danger"}>
          {row.status}
        </Badge>
      ),
    },
    {
      prop: "created_at",
      title: "Created Date",
      isFilterable: true,
      isSortable: true,
      cell: (row) => formatDateToDDMMYYYY(row.created_at) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-dark"
            onClick={() => handleEditSubCategory(row.id)}
            disabled={isUpdating || isDeleting}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteSubCategory(row.id, row.name)}
            disabled={isUpdating || isDeleting}
          >
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading subcategories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SubCategoryModal
        show={showModal}
        handleClose={handleCloseModal}
        editId={editingSubCategory}
      />

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDeleteSubCategory}
        onConfirm={confirmDeleteSubCategory}
        title="Delete Subcategory"
        message={`Are you sure you want to delete the subcategory "${subCategoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Subcategories
        </Link>
      </div>

      <DatatableWrapper
        body={filteredSubCategories || []}
        headers={headers || []}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
              <Filter placeholder="Search subcategories..." className="w-100" />
            </div>
            {/* Category Filter */}
            <div style={{ maxWidth: "200px" }}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Subcategories"
              right={filteredSubCategories?.length}
            />
          </div>

          <div className="d-flex align-items-center">
            <Button
              onClick={handleCreateSubCategory}
              variant="dark"
              className="btn-sm"
            >
              New Subcategory
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default SubCategory;
